import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";
import { RiAppStoreFill, RiGooglePlayFill } from "react-icons/ri";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import logo from "../assets/img/CheckIDBioSense_light.svg";
import { SendButton } from "../components/button/SendButton";
import { fetchCsrfToken } from "../utils/api";

const TYPE_OPTIONS = [
    { value: "apk", labelKey: "uploads.android", icon: RiGooglePlayFill, color: "var(--tree-poppy)" },
    { value: "ipa", labelKey: "uploads.ios", icon: RiAppStoreFill, color: "var(--tree-leaf)" },
];

const ENV_OPTIONS = [
    { value: "production", labelKey: "uploads.production" },
    { value: "demo", labelKey: "uploads.demo" },
];

const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Uploads = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [authChecked, setAuthChecked] = useState(false);
    const [environment, setEnvironment] = useState("production");
    const [type, setType] = useState("apk");
    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const IOS_EXTENSIONS = [".ipa", ".plist"];
    const getAccept = (platformType) =>
        platformType === "apk" ? ".apk" : ".ipa,.plist";
    // trong component:
    const accept = getAccept(type);

    useEffect(() => {
        axios
            .get("/uploads/session", { withCredentials: true })
            .then((res) => {
                if (!res.data?.authenticated) {
                    navigate("/uploads/login", { replace: true });
                    return;
                }
                setAuthChecked(true);
            })
            .catch(() => navigate("/uploads/login", { replace: true }));
    }, [navigate]);

    const isAllowedFile = (selectedFile, platformType) => {
        const name = selectedFile.name.toLowerCase();
        if (platformType === "apk") return name.endsWith(".apk");
        return IOS_EXTENSIONS.some((ext) => name.endsWith(ext));
    };

    const handleFile = (selectedFile) => {
        if (!selectedFile) return;

        if (!isAllowedFile(selectedFile, type)) {
            toast.error(t("uploads.invalidFile"));
            return;
        }

        setFile(selectedFile);
    };

    const handleTypeChange = (newType) => {
        setType(newType);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleEnvironmentChange = (newEnv) => {
        setEnvironment(newEnv);
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        handleFile(e.dataTransfer.files?.[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!file) return;
        const formData = new FormData();
        formData.append("type", type);
        formData.append("environment", environment);
        formData.append("file", file);
        setIsDisabled(true);
        try {
            // Lấy CSRF token (dùng GET /api/contact như contact form)
            await fetchCsrfToken();
            const csrfRes = await axios.get("/api/contact", { withCredentials: true });
            const token = csrfRes.headers["x-csrf-token"]?.split(",")[0];
            if (!token) {
                toast.error(t("uploads.fail"));
                return;
            }
            const res = await axios.post("/upload/api/private", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                    "X-CSRF-TOKEN": token,
                },
                withCredentials: true,
                timeout: 300000,
            });
            if (String(res.data) === "0") {
                toast.success(t("uploads.success"));
                setFile(null);
                if (fileInputRef.current) fileInputRef.current.value = "";
            } else {
                toast.error(t("uploads.fail"));
            }
        } catch (err) {
            if (err.response?.status === 401) {
                toast.error(t("uploads.sessionExpired"));
                navigate("/uploads/login", { replace: true });
            } else {
                toast.error(t("uploads.fail"));
            }
        } finally {
            setIsDisabled(false);
        }
    };

    const activeOption = TYPE_OPTIONS.find((opt) => opt.value === type);

    if (!authChecked) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <p className="text-[#3A505F] text-sm">{t("uploads.login.checking")}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-10 md:py-20 bg-gray-50 overflow-y-auto">
            <div className="w-full max-w-xl text-center">

                {/* Logo */}
                <img
                    src={logo}
                    className="h-[80px] md:h-[90px] mx-auto mb-8 object-contain"
                    alt="CheckID BioSense"
                />

                {/* Title */}
                <h1 className="text-[#1C2045] text-2xl md:text-3xl font-extrabold mb-2">
                    <span className="inline-block border-b-[6px] border-[var(--tree-poppy)] pb-1">
                        {t("uploads.title")}
                    </span>
                </h1>
                <p className="text-[#3A505F] text-sm md:text-base mb-8 max-w-md mx-auto leading-relaxed">
                    {t("uploads.description")}
                </p>

                {/* Card */}
                <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 text-left">
                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* Environment tabs: production | demo */}
                        <div>
                            <label className="block text-sm font-semibold text-[#1C2045] mb-3">
                                {t("uploads.environment")}
                            </label>
                            <div className="flex justify-center gap-3">
                                {ENV_OPTIONS.map((opt) => {
                                    const isActive = environment === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => handleEnvironmentChange(opt.value)}
                                            className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${isActive
                                                ? "bg-[var(--tree-poppy)] text-white shadow-md scale-105"
                                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                }`}
                                        >
                                            {t(opt.labelKey)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Platform tabs */}
                        <div>
                            <label className="block text-sm font-semibold text-[#1C2045] mb-3">
                                {t("uploads.platform")}
                            </label>
                            <div className="flex justify-center gap-3">
                                {TYPE_OPTIONS.map((opt) => {
                                    const Icon = opt.icon;
                                    const isActive = type === opt.value;
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => handleTypeChange(opt.value)}
                                            className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 ${isActive
                                                ? "bg-[var(--tree-leaf)] text-white shadow-md scale-105"
                                                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                                }`}
                                        >
                                            <Icon size={18} />
                                            {t(opt.labelKey)}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Drop zone */}
                        <div>
                            <label className="block text-sm font-semibold text-[#1C2045] mb-3">
                                {t("uploads.file")}
                            </label>

                            <div
                                role="button"
                                tabIndex={0}
                                onClick={() => !isDisabled && fileInputRef.current?.click()}
                                onKeyDown={(e) => e.key === "Enter" && fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                                onDrop={handleDrop}
                                className={`relative rounded-2xl border-2 border-dashed p-8 transition-all cursor-pointer
                  ${isDragging
                                        ? "border-[var(--tree-leaf)] bg-[#f1f6fd]"
                                        : file
                                            ? "border-[var(--tree-poppy)] bg-[#fff8f0]"
                                            : "border-gray-300 bg-[#f1f6fd] hover:border-[var(--tree-leaf)] hover:bg-white"
                                    }
                  ${isDisabled ? "opacity-60 pointer-events-none" : ""}
                `}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    accept={accept}
                                    onChange={(e) => handleFile(e.target.files?.[0])}
                                />

                                {!file ? (
                                    <div className="flex flex-col items-center gap-4 text-center py-4">
                                        {/* Vòng tròn logo ở giữa */}
                                        <div
                                            className={`relative w-24 h-24 md:w-28 md:h-28 rounded-full flex items-center justify-center
        shadow-md border-4 border-white transition-transform duration-200
        ${isDragging ? "scale-110" : ""}`}
                                            style={{
                                                background: `linear-gradient(135deg, ${activeOption?.color}22, ${activeOption?.color}44)`,
                                                boxShadow: `0 8px 24px ${activeOption?.color}33`,
                                            }}
                                        >
                                            {/* Logo theo platform đang chọn */}
                                            {type === "apk" ? (
                                                <RiGooglePlayFill size={40} style={{ color: "var(--tree-poppy)" }} />
                                            ) : (
                                                <RiAppStoreFill size={40} style={{ color: "var(--tree-leaf)" }} />
                                            )}
                                            {/* Badge upload nhỏ góc dưới phải (tuỳ chọn) */}
                                            <div
                                                className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-white shadow flex items-center justify-center"
                                            >
                                                <CloudUploadOutlinedIcon
                                                    sx={{ fontSize: 20, color: activeOption?.color }}
                                                />
                                            </div>
                                        </div>
                                        <p className="text-[#1C2045] font-semibold text-sm md:text-base">
                                            {t("uploads.dragDrop")}
                                        </p>
                                        <p className="text-gray-500 text-xs md:text-sm">
                                            {type === "apk" ? ".apk" : ".ipa, .plist"} — {t("uploads.maxSize")}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-4 py-2">
                                        {/* Vòng tròn icon — giống empty state, nhỏ hơn một chút */}
                                        <div
                                            className="relative w-16 h-16 md:w-20 md:h-20 rounded-full flex items-center justify-center flex-shrink-0
        shadow-md border-4 border-white"
                                            style={{
                                                background: `linear-gradient(135deg, ${activeOption?.color}22, ${activeOption?.color}44)`,
                                                boxShadow: `0 6px 16px ${activeOption?.color}33`,
                                            }}
                                        >
                                            {type === "apk" ? (
                                                <RiGooglePlayFill size={32} style={{ color: "var(--tree-poppy)" }} />
                                            ) : (
                                                <RiAppStoreFill size={32} style={{ color: "var(--tree-leaf)" }} />
                                            )}
                                        </div>

                                        <div className="flex-1 min-w-0 text-left">
                                            <p className="text-[#1C2045] font-semibold text-sm truncate">
                                                {file.name}
                                            </p>
                                            <p className="text-gray-500 text-xs">
                                                {formatFileSize(file.size)}
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFile(null);
                                                if (fileInputRef.current) fileInputRef.current.value = "";
                                            }}
                                            className="text-gray-400 hover:text-[var(--tree-poppy)] text-sm font-medium flex-shrink-0"
                                        >
                                            {t("uploads.remove")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Submit */}
                        <div className="flex justify-center pt-2">
                            <SendButton
                                title={isDisabled ? t("uploads.uploading") : t("uploads.submit")}
                                onClick={handleSubmit}
                                disabled={!file || isDisabled}
                            />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Uploads;